<template>
  <div>
    <Dropdown
      :data-test-id="$testId('savedViewDropdown')"
      :isMenuOpen="isSavedViewsOpen"
      @clickaway="handleSavedViewClose"
    >
      <template v-slot:button>
        <Button
          v-if="showAdd"
          :class="$style.savedViewButton"
          :data-test-id="$testId('savedViewAddButton')"
          hasOutline
          :isDisabled="!canBeSaved"
          :isLoading="loadingViews"
          text="Save View"
          type="secondary"
          @click="handleSavedViewSaveClick"
        />
        <IconButton
          v-else
          :class="$style.savedViewButton"
          :data-test-id="$testId('savedViewDropdownButton')"
          hasOutline
          icon="caretDown"
          iconPosition="right"
          :scale="0.5"
          :text="savedViewButtonText"
          type="secondary"
          @click="handleSavedViewDropdownClick"
        />
      </template>
      <template v-slot:menu>
        <div
          v-if="!loadingViews"
          :class="$style.dropdownContent"
        >
          <div :class="$style.savedViewsWrapper">
            <HeadingExtraSmall>
              Saved Views
            </HeadingExtraSmall>
            <div
              v-for="(view, index) in savedViewsSorted"
              :key="index"
              :class="$style.savedViewOption"
            >
              <ButtonLink
                :class="$style.savedViewLink"
                :data-test-id="$testId(`savedViewLink__${index}`)"
                :text="viewName(view)"
                textCase="lowercase"
                @click="() => handleSavedViewOptionClick(view)"
              />
              <LoadingIcon
                v-if="isLoading(view)"
                :data-test-id="$testId(`loadingIndicator__${index}`)"
                :isCloud="false"
                :scale="1.5"
              />
              <IconButton
                v-else
                ariaLabel="delete view"
                :data-test-id="$testId(`savedViewDeleteButton__${index}`)"
                hasNoBackground
                icon="delete"
                :scale="0.5"
                @click="() => handleDeleteClick(view)"
              />
            </div>
          </div>
          <div :class="$style.footer">
            <ButtonNoBackground
              :class="$style.saveAsButton"
              :data-test-id="$testId('saveAsButton')"
              :hasOutline="false"
              :isDisabled="!canBeSavedAs"
              text="Save As"
              type="secondary"
              @click="handleSavedViewSaveAsClick"
            />
            <Button
              :class="$style.saveButton"
              :data-test-id="$testId('saveButton')"
              hasOutline
              :isDisabled="!canBeSaved"
              text="Save View"
              type="secondary"
              @click="handleSavedViewSaveClick"
            />
          </div>
        </div>
        <div
          v-else
          :class="$style.dropdownContent"
        >
          <LoadingIcon
            :data-test-id="$testId('loadingIndicator')"
            :isCloud="false"
            :scale="2"
          />
        </div>
      </template>
    </Dropdown>
    <CloudInsightsAddViewModal
      v-if="showModal"
      :isEdit="isEdit"
      :loading="loadingSave"
      :savedView="selectedView"
      :showModal="showModal"
      @cancel="handleCancel"
      @edit="handleEdit"
      @save="handleSave"
    />
  </div>
</template>

<script>
import {
  Button,
  ButtonLink,
  ButtonNoBackground,
  Dropdown,
  HeadingExtraSmall,
  IconButton,
  LoadingIcon,
} from '@jumpcloud/ui-components';
import { find, isEqual } from 'lodash';
import {
  mapActions,
  mapGetters,
  mapState,
} from 'vuex';
import { notify } from 'client/util';
import CloudInsightsAddViewModal from 'client/CloudInsights/CloudInsightsItemList/CloudInsightsAddViewModal.vue';

const cloudInsightsEventsModule = 'CloudInsightsEventsCollection';

export default {
  name: 'CloudInsightsSavedViewsDropdown',

  components: {
    Button,
    ButtonNoBackground,
    ButtonLink,
    CloudInsightsAddViewModal,
    Dropdown,
    HeadingExtraSmall,
    IconButton,
    LoadingIcon,
  },

  props: {
    nonPersonEntityFilter: {
      type: Boolean,
      required: true,
    },
    resetKey: {
      type: Number,
      required: true,
    },
    selectedColumns: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      deletedId: null,
      deleteLoading: false,
      isEdit: false,
      isSavedViewsOpen: false,
      loadingSave: false,
      loadingViews: false,
      selectedView: null,
      showModal: false,
    };
  },

  computed: {
    ...mapGetters(cloudInsightsEventsModule, [
      'getFilters',
    ]),
    ...mapState(cloudInsightsEventsModule, [
      'savedViews',
      'selectedSavedView',
    ]),

    canBeSaved() {
      if (!this.selectedView) return true;
      return !isEqual(this.selectedView.columns, this.selectedColumns)
        || !isEqual(this.selectedView.filters, this.getFilters)
        || !isEqual(this.selectedView.excludeNonPersonEntities, this.nonPersonEntityFilter);
    },

    canBeSavedAs() {
      return this.selectedView !== null;
    },

    savedViewButtonText() {
      if (this.selectedView) {
        const { name } = this.selectedView;
        if (name.length > 15) {
          return `${name.substring(0, 15)}...`;
        }
        return name;
      }
      return 'Saved Views';
    },

    savedViewsSorted() {
      const views = [...this.savedViews];
      // eslint-disable-next-line no-confusing-arrow, no-nested-ternary
      return views.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
    },

    showAdd() {
      return this.savedViews && !this.savedViews.length;
    },
  },

  watch: {
    resetKey: {
      handler() {
        this.reset();
      },
    },
  },

  mounted() {
    this.loadInitialData();
  },

  created() {
    // In the event that user navigates to another JC page and back,
    // we want to show their same selected saved view.
    // The selected filters associated to this saved view and any modifications to it
    // are re-selected via the `defaultFilters` prop
    // in CloudInsightsItemList -> ItemListCollectionWrapper
    if (this.savedViews.length && this.selectedSavedView) {
      this.selectedView = find(
        this.savedViews,
        (view) => view.viewId === this.selectedSavedView,
      );
    }
  },

  methods: {
    ...mapActions(cloudInsightsEventsModule, [
      'deleteSavedView',
      'fetchSavedViews',
      'saveView',
      'updateSavedView',
    ]),

    handleCancel() {
      this.showModal = false;
    },

    async handleDeleteClick(view) {
      this.deletedId = view.viewId;
      this.deleteLoading = true;
      try {
        await this.deleteSavedView({ id: view.viewId });
        if (this.selectedView && this.selectedView.name === view.name) {
          this.selectedView = null;
          this.$emit('resetSavedView');
        }
      } catch {
        notify('error', 'Unable to delete save view');
      } finally {
        this.deleteLoading = false;
        this.deletedId = null;
      }
    },

    async handleEdit() {
      this.isSavedViewsOpen = false;
      this.loadingSave = true;

      const payload = {
        columns: this.selectedColumns,
        filters: this.getFilters,
        nonPersonEntity: this.nonPersonEntityFilter,
        viewName: this.selectedView.name,
        viewId: this.selectedView.viewId,
      };

      try {
        const view = await this.updateSavedView({ savedView: payload });
        this.selectedView = view;
        // Refetch saved views to update list
        await this.fetchSavedViews();
      } catch {
        notify('error', 'Unable to modify saved view');
      } finally {
        this.loadingSave = false;
        this.showModal = false;
      }
    },

    async handleSave(viewName) {
      this.isSavedViewsOpen = false;
      this.loadingSave = true;

      const payload = {
        columns: this.selectedColumns,
        filters: this.getFilters,
        nonPersonEntity: this.nonPersonEntityFilter,
        viewName,
      };

      try {
        const view = await this.saveView(payload);
        this.selectedView = view;
      } catch {
        notify('error', 'Unable to save view');
      } finally {
        this.loadingSave = false;
        this.showModal = false;
      }
    },

    handleSavedViewClose() {
      this.isSavedViewsOpen = false;
    },

    handleSavedViewDropdownClick() {
      this.isSavedViewsOpen = !this.isSavedViewsOpen;
    },

    handleSavedViewOptionClick(view) {
      this.isSavedViewsOpen = false;
      this.selectedView = view;
      this.$emit('savedViewApply', view);
      this.showModal = false;
    },

    handleSavedViewSaveAsClick() {
      this.isEdit = false;
      this.isSavedViewsOpen = false;
      this.showModal = true;
    },

    handleSavedViewSaveClick() {
      if (this.selectedView) {
        this.isEdit = true;
      }
      this.isSavedViewsOpen = false;
      this.showModal = true;
    },

    isLoading(view) {
      return view.viewId === this.deletedId;
    },

    async loadInitialData() {
      this.loadingViews = true;
      try {
        await this.fetchSavedViews();
      } catch {
        notify('error', 'Unable to fetch saved views');
      } finally {
        this.loadingViews = false;
      }
    },

    reset() {
      this.selectedView = null;
      this.$emit('resetSavedView');
    },

    viewName(view) {
      const { name } = view;
      if (name.length > 15) {
        return `${name.substring(0, 15)}...`;
      }
      return name;
    },
  },
};
</script>

<style scoped module>
.dropdownContent {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: var(--jc-border-radius);
  left: var(--jc-spacer-small);
  margin: var(--jc-spacer-large) 0;
  padding: 16px;
  position: absolute;
  top: 8px;
  z-index: 7;
}

.footer {
  display: flex;
}

.saveButton {
  min-width: 7rem;
}

.saveAsButton {
  margin-right: var(--jc-spacer-small);
  min-width: 6rem;
}

.savedViewButton {
  margin-left: var(--jc-spacer-small);
  margin-right: var(--jc-spacer-small);
}

.savedViewLink {
  display: block;
  margin-bottom: var(--jc-spacer-small);
  margin-right: var(--jc-spacer-small);
}

.savedViewOption {
  align-items: flex-end;
  display: flex;
  height: 32px;
  justify-content: space-between;
}

.savedViewsWrapper {
  margin-bottom: var(--jc-spacer-large);
}
</style>
